import styled, { useTheme, css } from '@sevenrooms/ui-kit'
import { IconButton } from '@sevenrooms/ui-kit/form'
import { useMaxWidthBreakpoint, useScrollController } from '@sevenrooms/ui-kit/hooks'
import { Flex, HStack, VStack } from '@sevenrooms/ui-kit/layout'
import type { ThemeSpacing } from '@sevenrooms/ui-kit/theme'
import type { PropsWithChildren, ReactNode } from 'react'

export interface RowContainerWithScrollProps {
  label?: ReactNode
  childWidth?: string
  spacing: ThemeSpacing
  scrollLeftLabel?: string
  scrollRightLabel?: string
  marginRight?: ThemeSpacing
  'data-test': string
}

export function RowContainerWithScroll({
  label,
  childWidth = '100%',
  spacing,
  scrollLeftLabel,
  scrollRightLabel,
  marginRight,
  children,
  'data-test': dataTest,
}: PropsWithChildren<RowContainerWithScrollProps>) {
  const { spacing: themeSpacing } = useTheme()
  const isMobile = useMaxWidthBreakpoint('s')
  const scrollStep = (parseInt(childWidth) + parseInt(themeSpacing[spacing])) * 2 + 1
  const { ref, scrollLeft, scrollRight, canScrollLeft, canScrollRight } = useScrollController(scrollStep, 'smooth')
  return (
    <StyledContainer spacing={label ? 'm' : 'none'} marginRight={marginRight}>
      <HStack justifyContent={label ? 'space-between' : 'right'} alignItems="center" height="min-content">
        {label && <Flex width="100%">{label}</Flex>}
        {!isMobile && (canScrollLeft || canScrollRight) && (
          <Flex flex="0 0 auto" ml="s" mr={marginRight}>
            <IconButton
              borderType="none-round"
              size="s"
              icon="VMSWeb-chevron-left"
              data-test="sr-room-card-left-button"
              aria-label={scrollLeftLabel ?? 'scroll left'}
              onClick={scrollLeft}
              disabled={!canScrollLeft}
            />
            <IconButton
              borderType="none-round"
              size="s"
              icon="VMSWeb-chevron-right"
              data-test="sr-room-card-right-button"
              aria-label={scrollRightLabel ?? 'scroll right'}
              onClick={scrollRight}
              disabled={!canScrollRight}
            />
          </Flex>
        )}
      </HStack>
      <StyledScrollStack
        data-test={dataTest}
        isMobile={isMobile}
        width="100%"
        childWidth={childWidth}
        spacing={spacing}
        justifyContent="flex-start"
        flexWrap="nowrap"
        ref={ref}
      >
        {children}
      </StyledScrollStack>
    </StyledContainer>
  )
}

const StyledContainer = styled(VStack)<{ marginRight?: ThemeSpacing }>`
  ${({ marginRight }) =>
    marginRight &&
    css`
      margin-right: -${props => props.theme.spacing[marginRight]};
    `}
`

const StyledScrollStack = styled(HStack)<{ isMobile: boolean; childWidth: string }>`
  overflow-x: scroll;
  ${({ isMobile }) =>
    !isMobile &&
    css`
      -ms-overflow-style: none;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    `}
  ${({ childWidth }) =>
    childWidth === '100%'
      ? css`
          > * {
            flex: 1 1 0;
            min-width: 0;
          }
        `
      : css`
          > * {
            width: ${childWidth};
            min-width: ${childWidth};
          }
        `}
`
